import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, CircularProgress, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import superAdminService from '../services/superAdminService';

const SuperAdminDealers = () => {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Form State
  const [formData, setFormData] = useState({
    companyName: '',
    brandName: '', 
    name: '', 
    email: '',
    password: '',
    language: 'en'
  });

  // Fetch function
  const fetchDealers = async () => {
    try {
      const data = await superAdminService.getAllDealers();
      setDealers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load dealers", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, []);

  const handleCreate = async () => {
    setError('');
    
    // Basic Validation
    if (!formData.companyName || !formData.name || !formData.email || !formData.password) {
        setError("All fields (except Brand) are required.");
        return;
    }
    if (formData.password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
    }

    setSubmitting(true);
    try {
        await superAdminService.createDealer(formData);
        
        // Success: Close, Refresh, Reset
        setOpen(false);
        setFormData({ companyName: '', brandName: '', name: '', email: '', password: '', language: 'en' });
        fetchDealers(); // Refresh the list
        alert("Dealer created successfully!");
    } catch (err) {
        setError(err.message);
    } finally {
        setSubmitting(false);
    }
  };

  const handleDeleteDealer = async (dealer) => {
    if (!window.confirm(`⚠️ DANGER ZONE ⚠️\n\nAre you sure you want to delete "${dealer.name}"?\n\nThis will PERMANENTLY delete:\n- The Dealer Account\n- All associated User Accounts\n- All Quotes and Surveys\n\nThis action cannot be undone.`)) {
        return;
    }

    try {
        await superAdminService.deleteDealer(dealer.id || dealer.Id);
        setDealers(prev => prev.filter(d => (d.id || d.Id) !== (dealer.id || dealer.Id)));
    } catch (error) {
        alert("Failed to delete dealer: " + error.message);
    }
  };


  if (loading) return <CircularProgress sx={{ m: 5 }} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/super-admin/dashboard')}>
            Back to Dashboard
        </Button>
        
        {/* CREATE DEALER BUTTON */}
        <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpen(true)}
            sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
        >
            Add New Dealer
        </Button>
      </Box>
      
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Registered Dealers
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Dealer Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Brand</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Users</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Surveys</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Quotes</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dealers.map((dealer, index) => (
              <TableRow key={index} hover>
                <TableCell sx={{ fontWeight: 'bold' }}>
                    {dealer.name || dealer.Name || 'Unnamed'}
                    <Typography variant="caption" display="block" color="text.secondary">
                        {dealer.contactEmail || dealer.ContactEmail}
                    </Typography>
                </TableCell>
                <TableCell>{dealer.brandName || dealer.BrandName || '-'}</TableCell>
                <TableCell><Chip label={dealer.userCount || 0} color="primary" size="small" /></TableCell>
                <TableCell><Chip label={dealer.surveyCount || 0} color="secondary" size="small" /></TableCell>
                <TableCell><Chip label={dealer.quoteCount || 0} color="default" size="small" /></TableCell>
                <TableCell>

                  <Box sx={{ display: 'flex', gap: 1 }}>

                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/super-admin/dealers/${dealer.id || dealer.Id}`)}
                    >
                        Details
                    </Button>

                    {/* DELETE BUTTON */}
                    <Button 
                        variant="outlined" 
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteDealer(dealer)}
                        sx={{ ml: 1 }}
                    >
                        Delete
                    </Button>

                  </Box>

                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* CREATE DEALER MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Onboard New Dealer</DialogTitle>
        <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
            
            <TextField 
                autoFocus margin="dense" label="Company Legal Name" fullWidth variant="outlined" 
                value={formData.companyName}
                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
            />
            <TextField 
                margin="dense" label="Brand Name (Optional)" fullWidth variant="outlined" 
                helperText="If different from Company Name (e.g. 'Prestige Canopies')"
                value={formData.brandName}
                onChange={(e) => setFormData({...formData, brandName: e.target.value})}
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#a8851d', fontWeight: 'bold' }}>
                Primary Admin Account
            </Typography>
            
            <TextField 
                margin="dense" label="Admin Full Name" fullWidth variant="outlined" 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
            <TextField 
                margin="dense" label="Admin Email" type="email" fullWidth variant="outlined" 
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
            <TextField 
                margin="dense" label="Initial Password" type="password" fullWidth variant="outlined" 
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
            />

            {/* Language Selector */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#a8851d', fontWeight: 'bold' }}>
                Configurator Language
            </Typography>
            <ToggleButtonGroup
                value={formData.language}
                exclusive
                onChange={(e, val) => { if (val) setFormData({...formData, language: val}); }}
                fullWidth
                sx={{ mb: 1 }}
            >
                <ToggleButton value="en">🇬🇧 English</ToggleButton>
                <ToggleButton value="nl">🇳🇱 Nederlands</ToggleButton>
            </ToggleButtonGroup>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
            <Button 
                onClick={handleCreate} 
                variant="contained" 
                disabled={submitting}
                sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
            >
                {submitting ? 'Creating...' : 'Create Dealer'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDealers;